import {
  Heading2 as ElementHeading2,
  Li,
  Margin,
  Ol,
  P,
  Strong,
} from '@ovotech/element-v4';
import { theme } from '@ovotech/element-v5';
import {
  EV,
  isSelectedPlanMonthlyPlan,
  useChargeAnytimeContext,
} from '@monovo/ev-anytime';
import React, { useEffect } from 'react';

import { PageWithNavigation } from '@/src/components';
import { Media } from '@/src/components/Media';
import {
  OVO_CHARGE_APP_STORE_URL,
  OVO_CHARGE_GOOGLE_PLAY_URL,
} from '@/src/constants/links';
import { PageHeader } from '@/src/pages/AnytimeEligibilityV2/components/PageHeader';
import OvoChargeQRCodeImg from '@/src/resources/chargeAnytime/ovo-charge-app-qr-code.png';
import { ReactComponent as AppStoreBadge } from '@/src/resources/apple-store-badge.svg';
import { ReactComponent as GooglePlayBadge } from '@/src/resources/google-play-badge.svg';
import ChargeAnytimeAnywhere from '@/src/resources/ovo-charge-large.png';
import { getDeviceOS } from '@/src/utils/getDeviceOS';
import { breakpoints } from '@/src/utils/mediaQuery';

import {
  DesktopContainer,
  LeftContent,
  MobileContainer,
  RightContent,
} from './OvoChargeAppLink.styled';
import { useAnalytics } from '@ovotech/ui-tools';

export const OvoChargeQRCode = () => {
  return (
    <img
      data-testid={'ovo-charge-qr-code'}
      src={OvoChargeQRCodeImg}
      alt="OVO Charge QR Code"
      width="140"
      height="140"
    />
  );
};

const HowToGet = () => {
  const deviceOS = getDeviceOS();
  const { selectedPlanId } = useChargeAnytimeContext();

  const firstProductString = isSelectedPlanMonthlyPlan(selectedPlanId)
    ? 'your monthly plan'
    : 'pay as you go';
  const secondProductString = isSelectedPlanMonthlyPlan(selectedPlanId)
    ? 'your plan'
    : 'pay as you go';

  return (
    <>
      <P>
        <Strong>To get {firstProductString}:</Strong>
      </P>
      <Margin left={2} bottom={2}>
        <Ol>
          <Li>
            Download the <Strong>OVO Charge</Strong> app <br />
          </Li>
          <Li>
            Activate <Strong>{secondProductString}</Strong> and start saving{' '}
          </Li>
        </Ol>
      </Margin>
      <Margin vertical={2}>
        <P> We'll guide you through set-up in the app.</P>
      </Margin>
      <Media query={breakpoints.mediumUp}>
        <P style={{ fontSize: theme.font.size.sm.mediaQuery.lg }}>
          <Strong>
            Scan the QR code to open or download the OVO Charge app
          </Strong>
        </P>
        <Margin top={3}>
          <OvoChargeQRCode />
        </Margin>
      </Media>
      <Media query={breakpoints.mediumDown}>
        {deviceOS !== 'android' && (
          <a
            target="_blank"
            rel="noreferrer"
            data-testid="app-store-badge"
            href={OVO_CHARGE_APP_STORE_URL}
          >
            <AppStoreBadge style={{ height: '50px' }} />
          </a>
        )}
        {deviceOS !== 'iOS' && (
          <a
            target="_blank"
            rel="noreferrer"
            data-testid="google-play-badge"
            href={OVO_CHARGE_GOOGLE_PLAY_URL}
          >
            <GooglePlayBadge style={{ height: '50px' }} />
          </a>
        )}
      </Media>
    </>
  );
};

const AppActivationContent = ({
  withMargin,
  isMobile,
}: {
  withMargin?: boolean;
  isMobile?: boolean;
}) => {
  const paragraph = (
    <P>
      You're all set for smarter charging. <Strong>Activate</Strong> in the{' '}
      <Strong>OVO Charge</Strong> app for cheaper rates.
    </P>
  );

  const headingStyle = {
    color: theme.color.neutral.black,
    fontSize: isMobile
      ? theme.font.size['xl'].mediaQuery.sm
      : theme.font.size['3xl'].mediaQuery.lg,
  };

  return (
    <>
      <ElementHeading2 style={headingStyle}>Activate in app.</ElementHeading2>
      {withMargin ? <Margin top={5}>{paragraph}</Margin> : paragraph}
    </>
  );
};

const ChargeAnytimeImage = () => (
  <img src={ChargeAnytimeAnywhere} alt="EV Anytime Charge Anywhere" />
);

const analyticsEventProperties = {
  Screen: EV.SCREENS.DOWNLOAD_OVO_CHARGE_APP,
  journeyName: 'Charge Anytime',
};

export const OvoChargeAppLink = () => {
  const { dispatch: analyticsDispatch } = useAnalytics();
  useEffect(() => {
    // event on mount
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.OVO_CHARGE_APP_DOWNLOAD_SCREEN_VIEWED,
      properties: analyticsEventProperties,
    });
    // `analyticsDispatch` method is not memoized, and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <PageWithNavigation title="Charge Anytime">
      <PageHeader
        data-testid="success-message"
        title={`Almost there!`}
        showDescription={false}
        useResponsiveSizes
      />
      <Media query={breakpoints.mediumUp}>
        <DesktopContainer>
          <LeftContent>
            <AppActivationContent withMargin />
            <HowToGet />
          </LeftContent>
          <RightContent>
            <ChargeAnytimeImage />
          </RightContent>
        </DesktopContainer>
      </Media>

      <Media query={breakpoints.mediumDown}>
        <MobileContainer>
          <AppActivationContent isMobile />
          <ChargeAnytimeImage />
          <HowToGet />
        </MobileContainer>
      </Media>
    </PageWithNavigation>
  );
};
